import formStyles from "../form-styles/styles.css";
import styles from "./styles.css";
import selectBox from "../../select-box";
import DOMPurify from "dompurify";
import SteppedForms from "../../stepped-form/browser";
import { remoteComponentPublish } from "../../remote-trigger/browser";
import { MODAL_HIDE_EVENT } from "../../modal/events";
import MapsService, {
  MapProviderType
} from "@churchofjesuschrist/maps-service";
import { configureMapsService } from "../../../utilities/maps-service-config";
import { geolocate } from "../../../utilities/geolocate";
import createLegacyAddressProvider, {
  getLegacyAddressVerifyAPI
} from "./providers/legacy";
import VerificationModalController from "./modals/verification-modal";
import PinDropModalController from "./modals/pin-drop-modal";
import PinDropMap from "./modals/pin-drop-map";
import {
  normalizeUSAddress,
  normalizeAPIResponse,
  normalizeAddressString,
  addressesMatch
} from "./utils/address-normalization";
import {
  isSmartyCountry,
  getTerritoryStateCode,
  getSmartyCountryForState
} from "../../../utilities/smarty-countries.js";
import {
  callbackWhenInView,
  initComponent,
  cleanFieldValue
} from "../../../utilities/common";

let isSmarty = false; // USA and the territories Smarty verifies
let isValidateCountry = false;
let nonUSAutoCompleteResponse = {};
let nonUSAutoCompleteResponseAddrCountry = {};
let nonUSAddressVerifyResponse = {};
let nonUSFullAddressWithWarning = "";
let nonUSAddressSelectionList = [];
let USAddressSelectionList = [];
let nonUSCountryMatchFlag = "";
let nonUSAddressValidationFlag = "";
let nonUSOptionDetailList = [];
let nonUSAutoCompleteSelected = {};
let smartyStreetAddressVerifiedResponse = {};

export default class AddressBlock {
  constructor(element) {
    this.element = element;
    this.emptyStrikes = 0;
    this.suppressNextAutocompleteRequest = false;
    this.pinDropConfirmedAddressSnapshot = "";
    this.mapsLanguage =
      window.PUBLIC_ENV?.lang || document.documentElement.lang || "en";
    this.mapsIsProd = window.PUBLIC_ENV?.LANE === "prod";
    // Also check for dev-env attribute like other map components
    if (element.hasAttribute("dev-env")) {
      this.mapsIsProd = false;
    }

    const geocountry = document
      ?.querySelector('meta[name="geocountry"]')
      ?.getAttribute("content");
    this.geoCountry = geocountry || "";

    this.preciselyValidateCountries = element.querySelector(
      `[data-type="precisely-validate-countries"]`
    );
    this.preciselyExcludeCountries = element.querySelector(
      `[data-type="precisely-exclude-countries"]`
    );
    this.preciselyIK = element.querySelector(`[data-type="precisely-ik"]`);
    this.autocompleteSetting = element.querySelector(
      `[data-type="autocompleteSetting"]`
    );
    this.nbrOfInitialChars = Number(
      element.querySelector(`[data-type="nbrOfInitialChars"]`)?.value
    );
    this.strikeLimit = Number(
      element.querySelector(`[data-type="strikeLimit"]`)?.value
    );
    this.triggerIntervalInChar = Number(
      element.querySelector(`[data-type="triggerIntervalInChar"]`)?.value
    );
    this.triggerIntervalInMilliSecond = element.querySelector(
      `[data-type="triggerIntervalInMilliSecond"]`
    );
    this.addrField = element.querySelector(
      `[data-type="address-block-street"]`
    );
    this.autocompleteWrapper = element.querySelector(
      "[data-autocomplete-wrapper]"
    );
    this.autocompleteArea = element.querySelector(
      `.${styles.autocompleteOptions}`
    );
    this.autocompleteOptions = [
      ...(this.autocompleteArea?.querySelectorAll(
        `[data-autocomplete-option]`
      ) || [])
    ];
    this.noAutocompleteOption = this.autocompleteArea?.querySelector(
      `[data-no-autocomplete]`
    );
    this.isSingleAddressField =
      this.autocompleteWrapper?.hasAttribute("data-single-field");
    this.countryWrapper = element.querySelector(
      `[data-type="address-block-country"]`
    );
    this.stateWrapper = element.querySelector(
      `[data-type="address-block-state"]`
    );

    // this.stateWrapper doesn't include the label and parent div; it's poorly named. This this.stateWrapperParent includes the whole block.
    this.stateWrapperParent = element.querySelector(`.${styles.stateWrapper}`);
    this.hiddenCountryLocationCode = element.querySelector(
      `[data-type="location-code"]`
    );
    this.hiddenAddressCountryISO3 = element.querySelector(
      `[data-type="address-country-ISO3"]`
    );
    this.addrLine2Field = element.querySelector(
      `[data-type="address-block-addrLine2"]`
    );

    this.revealer = element.querySelector(`[data-reveal-line2]`);
    this.pinDropRevealer = element.querySelector(`[data-reveal-pin-drop]`);
    this.addrLine2Area = element.querySelector(`[data-addr-line2-area]`);
    this.addrLine2Input = this.addrLine2Area?.querySelector("input");

    this.cityWrapper = element.querySelector(`.${styles.cityWrapper}`);
    this.cityField = element.querySelector(`[data-type="address-block-city"]`);

    this.zipField = element.querySelector(`[data-type="address-block-zip"]`);
    this.zipWrapper = element.querySelector(`.${styles.zipWrapper}`);
    this.cityStateZipContainer = element.querySelector(
      `.${styles.cityStateZipContainer}`
    );
    this.addr2Wrapper = element.querySelector(`.${styles.addr2Wrapper}`);
    // This checks if the zip field is too long and forces mobile styling if it is
    this.zipWrapper = element.querySelector(`.${styles.zipWrapper}`);
    if (this.zipWrapper) {
      callbackWhenInView({
        elems: [this.zipWrapper],
        callback: () => {
          const zipLabelWidth = element.querySelector(
            `[data-type="address-block-zip"] ~ label`
          )?.offsetWidth;
          let addressBlockWidth = this.element.clientWidth;

          if (
            this.zipWrapper &&
            zipLabelWidth >= this.zipWrapper?.clientWidth &&
            this.cityStateZipContainer
          )
            this.cityStateZipContainer.classList.add(styles.forceMobile);

          window.addEventListener("resize", () => {
            const zipWrapperWidth = this.zipWrapper?.clientWidth;
            if (
              zipLabelWidth < zipWrapperWidth &&
              zipWrapperWidth > addressBlockWidth
            ) {
              addressBlockWidth = this.element.clientWidth;
              if (this.cityStateZipContainer)
                this.cityStateZipContainer.classList.remove(styles.forceMobile);
            } else if (zipLabelWidth > zipWrapperWidth) {
              addressBlockWidth = this.element.clientWidth;
              if (this.cityStateZipContainer)
                this.cityStateZipContainer.classList.add(styles.forceMobile);
            }
          });
        }
      });
    }

    this.countryField =
      this.countryWrapper?.querySelector("[data-target-guid]");
    this.countryOverrideField = element.querySelector(
      `[data-type="country-override"]`
    );
    this.countryOverride = this.countryOverrideField?.value;

    const selectedCountry = this.countryField?.selectedOptions?.[0];
    const initialISO3 = this.countryField
      ? selectedCountry?.getAttribute("data-country-alpha3")
      : this.hiddenAddressCountryISO3?.value;
    isSmarty =
      isSmartyCountry(initialISO3) ||
      this.countryField?.value === "United States" ||
      this.countryOverride === "United States";

    this.stateField = this.stateWrapper?.querySelector("[data-target-guid]");

    this.stateSelect = element.querySelector("input[name=state]");

    this.stateValidation = this.stateWrapper?.querySelector(
      "[data-validity-message]"
    ).innerText;
    this.stateLabel = this.stateWrapper?.dataset.stateLabel || "";
    // variant for single to expandable fields
    this.expandableAddrFields = this.cityStateZipContainer?.hasAttribute(
      "data-expandable-addr-fields"
    );

    this.postalCode = element.querySelector(
      '[data-type="Typeahead-postalCode-input"]'
    );
    this.modalCloseBtn = element.querySelector("[data-modal-close]");
    this.modal = element.querySelector('[data-type="verification-modal"]');
    this.radioButtons = this.modal?.querySelectorAll('[type="radio"]') || [];
    this.addressArray = [];
    this.buttonWrapper = this.modal?.querySelector("[data-modal-buttons]");
    this.editButton = this.modal?.querySelector("[data-modal-edit]");
    this.continueButton = this.modal
      ?.querySelector("[data-modal-continue]")
      ?.querySelector('[data-type="button"]');
    this.noMatchText = this.modal?.querySelector("[data-no-match]");
    this.suggestedAddressText = this.modal?.querySelector(
      "[data-suggested-address]"
    );
    this.correctAddressText = this.modal?.querySelector(
      "[data-correct-address]"
    );
    this.addr1 = element.querySelector(
      '[data-type="Typeahead-AddressLine1-input"]'
    );
    this.typeaheadAddr2 = element.querySelector(
      '[data-type="Typeahead-AddressLine2-input"]'
    );
    this.typeaheadCity = element.querySelector(
      '[data-type="Typeahead-City-input"]'
    );
    this.typeaheadState = element.querySelector(
      '[data-type="Typeahead-State-input"]'
    );
    this.addr3 = element.querySelector(
      '[data-type="Typeahead-AddressLine3-input"]'
    );
    this.addr4 = element.querySelector(
      '[data-type="Typeahead-AddressLine4-input"]'
    );
    this.locality = element.querySelector(
      '[data-type="Typeahead-Locality-input"]'
    );
    this.postalCode = element.querySelector(
      '[data-type="Typeahead-PostalCode-input"]'
    );
    this.addressVerifyRecordId = element.querySelector(
      '[data-type="Precisely-addressVerifyRecordId"]'
    );
    this.addressVerifyResponseAddr1 = element.querySelector(
      '[data-type="Precisely-MainAddressLine"]'
    );
    this.addressVerifyResponseCity = element.querySelector(
      '[data-type="Precisely-AreaName3"]'
    );
    this.addressVerifyResponsePostalCode1 = element.querySelector(
      '[data-type="Precisely-PostCode1"]'
    );
    this.addressVerifyResponsePostalCode2 = element.querySelector(
      '[data-type="Precisely-PostCode2"]'
    );
    this.addressVerifyResponseState = element.querySelector(
      '[data-type="Precisely-AreaName1"]'
    );
    this.addressVerifyResponseCountryISO3 = element.querySelector(
      '[data-type="Precisely-Country"]'
    );
    this.addressVerifyResponseSingle = element.querySelector(
      '[data-type="Precisely-FormattedAddress"]'
    );
    this.addressVerifyLat = element.querySelector('[data-type="Precisely-X"]');
    this.addressVerifyLng = element.querySelector('[data-type="Precisely-Y"]');
    this.addressVerifyCountryComparisonFlag = element.querySelector(
      '[data-type="Precisely-CountryComparisonFlag "]'
    );
    this.addressVerifyResponsePrecisionLevel = element.querySelector(
      '[data-type="Precisely-PrecisionLevel"]'
    );
    this.addressVerifyResponsePrecisionCode = element.querySelector(
      '[data-type="Precisely-PrecisionCode"]'
    );
    this.addressVerifyMatch = element.querySelector(
      '[data-type="Precisely-Match"]'
    );
    this.addressVerifyResponseCountry = element.querySelector(
      '[data-type="Precisely-CountryName"]'
    );
    this.addressVerifyCountrySource = element.querySelector(
      '[data-type="Precisely-CountrySource"]'
    );
    this.addressVerifyQualityMet = element.querySelector(
      '[data-type="Precisely-LDS_AddressValidationFlag"]'
    );
    this.addressVerifyBestGeocodingCoverageByCountry = element.querySelector(
      '[data-type="Precisely-BestGeocodingCoverageByCountry"]'
    );
    this.addressVerifyAccepted = element.querySelector(
      '[data-type="Precisely-User_Confirmed_InputAddress"]'
    );
    this.addressSelections = element.querySelector("[verify-option-text]");
    this.likelyBadOneSuggestion = element.querySelector(
      '[data-type="AVM-likelyBadOneSuggestion"]'
    );
    this.likelyBadNoSuggestion = element.querySelector(
      '[data-type="AVM-likelyBadNoSuggestion"]'
    );
    this.confirmButtonLabel = element.querySelector(
      '[data-type="confirmButtonLabel"]'
    );
    this.defaultSuggestedAddressMessage = element.querySelector(
      '[data-type="defaultSuggestedAddressMessage"]'
    );
    this.autocompleteSelected = element.querySelector(
      '[data-type="autocompleteSelected"]'
    );
    this.validatedAddressSelected = element.querySelector(
      '[data-type="validatedAddressSelected"]'
    );
    this.validationAutocompleteSelected = element.querySelector(
      '[data-type="validationAutocompleteSelected"]'
    );
    this.pinDropLinkClicked = element.querySelector(
      '[data-type="pin-drop-link-clicked"]'
    );
    this.pinDropAddressConfirmed = element.querySelector(
      '[data-type="pin-drop-address-confirmed"]'
    );
    this.pinDropAddressSubmitted = element.querySelector(
      '[data-type="pin-drop-address-submitted"]'
    );
    this.validity = element.querySelector('[data-type="validity"]');
    this.addressProvider = element.querySelector(
      '[data-type="address-provider"]'
    );
    this.enablePinDrop = element.querySelector('[data-type="enable-pin-drop"]');
    this.pinDropModal = element.querySelector('[data-type="pin-drop-modal"]');
    this.geolocateBrowserUnsupported = element.querySelector(
      '[data-type="geolocate-browser-unsupported"]'
    );
    this.geolocateUserBlocked = element.querySelector(
      '[data-type="geolocate-user-blocked"]'
    );
    this.geolocateLookupError = element.querySelector(
      '[data-type="geolocate-lookup-error"]'
    );
    this.pinDropModalInvalidLocationMessage = element.querySelector(
      '[data-type="pin-drop-modal-invalid-location-message"]'
    );
    this.pinDropMapError = element.querySelector(
      '[data-type="pin-drop-map-error"]'
    );
    this.smartyVerifiedAddressLine = element.querySelector(
      '[data-type="smartyVerifiedAddressLine"]'
    );
    this.smartyVerifiedCity = element.querySelector(
      '[data-type="smartyVerifiedCity"]'
    );
    this.smartyVerifiedStateProvince = element.querySelector(
      '[data-type="smartyVerifiedStateProvince"]'
    );
    this.smartyVerifiedZipCode = element.querySelector(
      '[data-type="smartyVerifiedZipCode"]'
    );

    // Initialize legacy provider
    this.provider = createLegacyAddressProvider({
      component: this,
      getIsUSA: () => isSmarty,
      getIsValidateCountry: () => isValidateCountry,
      setAutocompleteResponse: (value) => {
        nonUSAutoCompleteResponse = value;
      },
      setAddressVerifyResponse: (value) => {
        nonUSAddressVerifyResponse = value;
      }
    });
    this.verificationModal = new VerificationModalController(this);
    this.pinDropModalController = new PinDropModalController(this);

    this.addressFields = [
      this.addrField,
      this.addrLine2Field,
      this.cityField,
      this.stateField,
      this.zipField
    ];

    this.expanded = false; // add listener the first time only there is value in the input for if they click off

    this.setupAutocompleteOptions();
    this.setupAddressField();
    this.setupPinDropFlow();
    this.setupListeners(this.addressFields);
    this.setupVerificationListeners();
    this.handleFieldDisabling(true);
    this.getCountries();
    if (geocountry) this.setupDefaultCountry(geocountry);

    element.getValues = () => this.getValues();
    element.checkIsValid = () => this.checkIsValid();
  }

  // Normalize US address from user input
  normalizeUSAddress() {
    return normalizeUSAddress(this);
  }

  // Normalize API response address
  static normalizeAPIResponse(apiResponse) {
    return normalizeAPIResponse(apiResponse, isSmarty);
  }

  // Normalize address string for comparison
  static normalizeAddressString(address) {
    return normalizeAddressString(address);
  }

  // Compare two normalized addresses
  static addressesMatch(userAddress, apiAddress) {
    return addressesMatch(userAddress, apiAddress);
  }

  /**
   * Measures the validity of address verification results and assigns a score
   *
   * Validity Scores:
   * - 10 (ADDRESSES_MATCH): User's address exactly matches API response - no user intervention needed
   * - 15 (API_ERROR): API service unavailable or returned error - proceed without verification
   * - 20 (ADDRESSES_DIFFER): User's address differs from API suggestion - user must choose
   *
   * @param {number} reqStatus - HTTP status code from API request
   * @param {Object|Array} addressVerifyResponse - Response from address verification API
   */
  measureValidity(reqStatus, addressVerifyResponse) {
    if (reqStatus !== 200) {
      // If Smarty or Precisely has an issue. Set the score to 15.
      this.validity.value = 15;
      return;
    }
    if (isSmarty) {
      if (Object.keys(addressVerifyResponse || {})?.length > 0) {
        const userAddress = this.normalizeUSAddress();
        const apiAddress = AddressBlock.normalizeAPIResponse(
          addressVerifyResponse[0]
        );
        if (AddressBlock.addressesMatch(userAddress, apiAddress)) {
          this.validity.value = 10; // Good - addresses match
        } else {
          this.validity.value = 20; // Possibly Good - addresses differ
        }
      } else {
        this.validity.value = 20; // Possibly Good for non-US addresses
      }
    } else {
      const output = addressVerifyResponse?.Output[0];
      if (output?.LDS_AddressValidationFlag === "T" && output?.Match !== "N") {
        this.validity.value = 10; // Good
      } else {
        this.validity.value = 20; // Possibly Good
      }
    }
  }

  setValidationFlags(inValue) {
    switch (inValue) {
      case "validatedAddressSelected":
        this.validatedAddressSelected.value = "true";
        this.validationAutocompleteSelected.value = "false";
        break;
      case "validationAutocompleteSelected":
        this.validatedAddressSelected.value = "false";
        this.validationAutocompleteSelected.value = "true";
        break;
      default:
        this.validatedAddressSelected.value = "false";
        this.validationAutocompleteSelected.value = "false";
    }
  }

  setIntlUserInputFields(index) {
    let entry =
      index === 0 ? nonUSAutoCompleteSelected : nonUSOptionDetailList[index];
    const isSPOD = Boolean(entry?.RecordID);
    if (isSPOD) {
      this.addr1.value = entry["AddressLine1.input"] || "";
      this.typeaheadAddr2.value = entry["AddressLine2.input"] || "";
      this.addr3.value = entry["AddressLine3.input"] || "";
      this.addr4.value = entry["AddressLine4.input"] || "";
      this.typeaheadCity.value = entry["City.input"] || "";
      this.typeaheadState.value = entry["StateProvince.input"] || "";
      this.locality.value = entry["Locality.input"] || "";
      this.postalCode.value = entry["PostalCode.input"] || "";
    } else {
      this.addr1.value = entry?.AddressLine1 || "";
      this.typeaheadAddr2.value = entry?.AddressLine2 || "";
      this.addr3.value = entry?.AddressLine3 || "";
      this.addr4.value = entry?.AddressLine4 || "";
      this.typeaheadCity.value = entry?.City || "";
      this.typeaheadState.value = entry?.StateProvince || "";
      this.locality.value = "";
      this.postalCode.value = entry?.PostalCode || "";
    }
  }

  setIntlUserInputFieldsFromSelectedAutocompleteOption(option) {
    this.addr1.value = option?.AddressLine1 || "";
    this.typeaheadAddr2.value = option?.AddressLine2 || "";
    this.addr3.value = option?.AddressLine3 || "";
    this.addr4.value = option?.AddressLine4 || "";
    this.typeaheadCity.value = option?.City || "";
    this.typeaheadState.value = option?.StateProvince || "";
    this.locality.value = "";
    this.postalCode.value = option?.PostalCode || "";
  }

  setPreciselyFields() {
    let output = nonUSAddressVerifyResponse?.Output[0];
    this.addressVerifyRecordId.value = output?.RecordID || "";
    this.addressVerifyResponseAddr1.value = output?.MainAddressLine || "";
    this.addressVerifyResponseCity.value = output?.AreaName3 || "";
    this.addressVerifyResponsePostalCode1.value = output?.PostCode1 || "";
    this.addressVerifyResponsePostalCode2.value = output?.PostCode2 || "";
    this.addressVerifyResponseState.value = output?.AreaName1 || "";
    this.addressVerifyResponseCountryISO3.value = output?.Country || "";
    this.addressVerifyResponseSingle.value = output?.FormattedAddress || "";
    this.addressVerifyLat.value = output?.X || "";
    this.addressVerifyLng.value = output?.Y || "";
    this.addressVerifyCountryComparisonFlag.value =
      output?.CountryComparisonFlag || "";
    this.addressVerifyResponsePrecisionLevel.value =
      output?.PrecisionLevel || "";
    this.addressVerifyResponsePrecisionCode.value = output?.PrecisionCode || "";
    this.addressVerifyMatch.value = output?.Match || "";
    this.addressVerifyResponseCountry.value = output?.CountryName || "";
    this.addressVerifyCountrySource.value = output?.CountrySource || "";
    this.addressVerifyQualityMet.value =
      output?.LDS_AddressValidationFlag || "";
    this.addressVerifyBestGeocodingCoverageByCountry.value =
      output?.BestGeocodingCoverageByCountry || "";
  }

  setSmartyFields() {
    // Check if smartyStreetAddressVerifiedResponse exists and has data
    if (
      !smartyStreetAddressVerifiedResponse ||
      !Array.isArray(smartyStreetAddressVerifiedResponse) ||
      smartyStreetAddressVerifiedResponse.length === 0
    ) {
      // Clear the fields if no SmartyStreets response (e.g., when using Google provider)
      this.smartyVerifiedAddressLine.value = "";
      this.smartyVerifiedCity.value = "";
      this.smartyVerifiedStateProvince.value = "";
      this.smartyVerifiedZipCode.value = "";
      return;
    }

    let output = smartyStreetAddressVerifiedResponse[0];
    this.smartyVerifiedAddressLine.value = output?.delivery_line_1 || "";
    this.smartyVerifiedCity.value = output?.components?.city_name || "";
    this.smartyVerifiedStateProvince.value =
      output?.components?.state_abbreviation || "";
    this.smartyVerifiedZipCode.value = output?.components?.zipcode || "";
  }

  setAddressFields() {
    this.addressFields =
      this.isSingleAddressField || isValidateCountry
        ? [this.addrField]
        : [
            this.addrField,
            this.addrLine2Field,
            this.cityField,
            this.stateField,
            this.zipField
          ];
  }

  handleExpansionFields() {
    if (!isValidateCountry) {
      if (this.addr2Wrapper) this.addr2Wrapper.hidden = false;
      if (this.cityStateZipContainer) this.cityStateZipContainer.hidden = false;
    }
  }

  navigateAutocompleteOptions(index) {
    const option = this.autocompleteOptions[index];
    if (option && !option.hidden) {
      option.focus();
    }
  }

  /* eslint-disable */
  intlAddressVerify(addr, countryComparisonFlag) {
    return this.provider.intlAddressVerify(addr, countryComparisonFlag);
  }

  preciselyik() {
    this.provider.fetchPreciselyIK();
  }

  /* eslint-enable */

  confirmedInputAddress(selectedAddress, selectedIndex) {
    if (!selectedAddress) return Promise.resolve();
    let addressVerification = Promise.resolve();

    if (selectedIndex === 0) {
      this.setValidationFlags("userEnteredAddressSelected");
    } else if (
      selectedAddress?.source === "autocomplete" ||
      selectedAddress?.country === "United States" ||
      selectedAddress?.isSmarty
    ) {
      this.setValidationFlags("validationAutocompleteSelected");
    } else {
      this.setValidationFlags("validatedAddressSelected");
    }

    if (!isValidateCountry) return addressVerification;

    // Handle non-US country match flag logic
    if (nonUSCountryMatchFlag === "N") {
      if (selectedIndex === 0 || selectedAddress?.source === "autocomplete") {
        // user selected user single line address or a typeahead address
        // resubmit the address to the SPOD with CountryComparisonFlag set to 'F' and use ISO3 country
        // set addressVerifyAccepted to 'N/A' (user has NOT verified their entered address over the SPOD address)
        addressVerification = this.intlAddressVerify(this.addrField.value, "F");
        this.addressVerifyAccepted.value = "N/A";
      } else if (selectedAddress.source === "addressVerify") {
        // user selected the SPOD suggested address, set addressVerifyAccepted to 'T'
        this.addressVerifyAccepted.value = "T";
      }
    } else {
      if (nonUSAddressValidationFlag === "T") {
        // if LDS_AddressValidationFlag = 'T', popup address suggestion will not be shown then
        // set addressVerifyAccepted to 'N/A'.
        this.addressVerifyAccepted.value = "N/A";
      } else if (nonUSAddressValidationFlag === "F") {
        if (selectedIndex === 0) {
          // if LDS_AddressValidationFlag = 'F' and user selected user single line address then
          // set addressVerifyAccepted to 'F'.
          addressVerification = this.intlAddressVerify(
            this.addrField.value,
            "T"
          ); // resubmit to the SPOD for validation
          this.addressVerifyAccepted.value = "F";
        } else if (selectedAddress?.source === "addressVerify") {
          // if LDS_AddressValidationFlag = 'F' and user selected SPOD suggested address then
          // set addressVerifyAccepted to 'T'.
          this.addressVerifyAccepted.value = "T";
          addressVerification = this.intlAddressVerify(
            this.addrField.value,
            "T"
          );
        } else {
          //  selectedAddress?.source === "autocomplete"
          // if LDS_AddressValidationFlag = 'F' and user selected a typeahead address then
          // set addressVerifyAccepted to 'N/A'.
          this.addressVerifyAccepted.value = "N/A";
          addressVerification = this.intlAddressVerify(
            this.addrField.value,
            "T"
          );
        }
      } else {
        this.addressVerifyAccepted.value = "N/A";
        addressVerification = this.intlAddressVerify(this.addrField.value, "T");
      }
    }
    this.setIntlUserInputFields(selectedIndex);
    return addressVerification;
  }

  getUSAutocompleteSuggestions() {
    return this.provider.getUSAutocompleteSuggestions();
  }

  bundleUSAddress() {
    return DOMPurify.sanitize(
      this.bundleAddress(
        this.addressFields.map((field) =>
          field && field !== this.countryField ? field.value : ""
        )
      ),
      {
        ALLOWED_TAGS: []
      }
    );
  }

  async addressValidation(resolve, reject) {
    nonUSAutoCompleteResponseAddrCountry = {};
    nonUSAddressVerifyResponse = {};
    let isValid = true;
    this.addressFields.forEach((field) => {
      if (field?.required && field?.value === "") isValid = false;
    });

    if (!isValid) {
      reject?.();
      return;
    }

    const api = isSmarty
      ? this.provider.getAddressVerifyAPI(this.bundleUSAddress())
      : this.provider.getAddressVerifyAPI(
          this.addrField.value,
          this.hiddenAddressCountryISO3.value
        );

    const req = new XMLHttpRequest();
    req.addEventListener("load", () => {
      if (isValidateCountry) {
        nonUSAddressVerifyResponse = req.response;
        this.setPreciselyFields();
        const countrySource =
          nonUSAddressVerifyResponse?.Output[0]?.CountrySource;
        nonUSCountryMatchFlag =
          countrySource?.toLowerCase() === "both sources match".toLowerCase() ||
          countrySource?.toLowerCase() === "ISO3 Country Code".toLowerCase()
            ? "Y"
            : "N";
        // If LDS_AddressValidation
        nonUSAddressValidationFlag =
          nonUSAddressVerifyResponse?.Output[0]?.LDS_AddressValidationFlag;
      } else {
        smartyStreetAddressVerifiedResponse = req.response;
        this.setSmartyFields();
      }
      // Check validity score before showing modal
      this.measureValidity(req.status, req.response);
      const validity = Number(this.validity.value);
      if (validity < 20) {
        // Addresses match (10) or API error (15) - no modal needed
        if (isSmarty && req.response?.[0]) {
          // An exact match can still be in another Smarty country (a Utah address typed under
          // Puerto Rico). countryOnChange clears the street, so put the typed one back.
          const street = this.addrField.value;
          this.selectSmartyCountry(
            req.response[0].components?.state_abbreviation
          );
          this.addrField.value = street;
        }
        this.fieldIsValid = true;
        this.awaitingAddressSelection = false;
        if (this.addressVerifyAccepted) {
          this.addressVerifyAccepted.value = "N/A";
        }
        this.dispatchValidationEvent(true);
        resolve?.();
        return;
      }

      if (isSmarty) {
        // Use SmartyStreets verification response if available, otherwise fall back to autocomplete
        if (req.response && req.response.length > 0) {
          // SmartyStreets found verification results - use them
          const taggedResponse = req.response.map((result) => ({
            ...result,
            _responseType: "verification"
          }));
          this.handleValidationModalDisplay(taggedResponse, resolve, reject);
        } else {
          // No SmartyStreets verification results - fall back to autocomplete suggestions
          this.getUSAutocompleteSuggestions().then(
            (autocompleteSuggestions) => {
              const taggedResponse = autocompleteSuggestions.map((result) => ({
                ...result,
                _responseType: "autocomplete"
              }));
              this.handleValidationModalDisplay(
                taggedResponse,
                resolve,
                reject
              );
            }
          );
        }
      } else if (isValidateCountry) {
        if (nonUSCountryMatchFlag === "N") {
          this.preHandleValidationModalDisplay(
            nonUSAddressVerifyResponse,
            resolve,
            reject
          );
        } else {
          this.handleValidationModalDisplay(
            nonUSAddressVerifyResponse,
            resolve,
            reject
          );
        }
      }
    });
    req.open("POST", api);
    req.responseType = "json";
    req.send();
  }

  async preHandleValidationModalDisplay(addressSuggestions, resolve, reject) {
    // For non-US, if ISO3 country doesn't match address country, get autocomplete suggestions using address country.
    if (isValidateCountry) {
      const country = addressSuggestions?.Output[0]?.CountryName;
      const autocompleteAPI = this.provider.getAutocompleteAPI(country);
      const req = new XMLHttpRequest();
      req.addEventListener("load", async () => {
        nonUSAutoCompleteResponseAddrCountry = req.response;
        await this.handleValidationModalDisplay(
          addressSuggestions,
          resolve,
          reject
        );
      });
      req.open("GET", autocompleteAPI);
      req.responseType = "json";
      req.send();
    }
  }

  async handleValidationModalDisplay(addressSuggestions, resolve, reject) {
    nonUSAddressSelectionList = [];
    USAddressSelectionList = [];
    nonUSOptionDetailList = [];
    this.verificationModal.resetForDisplay(styles);
    this.formattedAddresses = await this.formatAddressOptions(
      addressSuggestions
    );

    const verificationOptions = this.verificationModal.getVerificationOptions();

    // Display original address and suggested addresses with suggested changes
    const addrOptions = this.verificationModal.getAddressOptions();
    addrOptions.forEach((option) => {
      // clear all the suggested address and their warnings
      option.classList.remove(styles.warning);
      option.innerHTML = "";
    });

    verificationOptions.forEach((option, index) => {
      const verifyOptionsText = option.querySelector("[verify-option-text]");
      const radio = option.querySelector('input[type="radio"]');
      if (index === 0) {
        isSmarty
          ? USAddressSelectionList.push(this.bundleUSAddress())
          : nonUSAddressSelectionList.push(this.addrField.value);
        if (isValidateCountry && nonUSCountryMatchFlag === "N") {
          verifyOptionsText.innerHTML = nonUSFullAddressWithWarning;
        } else {
          verifyOptionsText.innerHTML = this.formattedAddresses[0]?.addr || "";
        }
      } else if (index > 0 && this.formattedAddresses[index]) {
        if (isValidateCountry) {
          nonUSAddressSelectionList.push(this.formattedAddresses[index]);
        } else if (isSmarty) {
          USAddressSelectionList.push(this.formattedAddresses[index]);
        }
        let comparedResult = this.compareAddresses(
          this.formattedAddresses[index]
        );
        verifyOptionsText.innerHTML = comparedResult;
        option.hidden = false;
        radio.hidden = false;
      } else {
        option.hidden = true;
      }
    });

    const visibleOptions = this.verificationModal.getVisibleOptions();
    const visibleRadioButtons = this.verificationModal.getVisibleRadioButtons();

    const validity = Number(this.validity.value);
    if (validity < 20) {
      // non USA if the validation flag is true and country matches, no need to show the validation modal.
      this.fieldIsValid = true;
      this.awaitingAddressSelection = false;
      if (this.addressVerifyAccepted) {
        this.addressVerifyAccepted.value = "N/A";
      }
      this.dispatchValidationEvent(true);
      resolve?.();
      return;
    }

    if (validity === 20) {
      this.awaitingAddressSelection = true;
      this.pendingAddressConfirmation = Promise.resolve();
      this.suggestedAddressText.innerHTML =
        this.defaultSuggestedAddressMessage.value;
      this.suggestedAddressText.hidden = false;
      this.continueButton.innerHTML = this.confirmButtonLabel.value;
      this.editButton.hidden = false; // show the try again button
      this.continueButton.hidden = false; // show the confirm button
      this.continueButton.disabled = false; // enable the confirm button
      if (!visibleOptions || visibleOptions?.length === 1) {
        // no suggestions found only user input address
        this.replaceAddressFieldValues(this.formattedAddresses?.[0], 0);
        if (visibleRadioButtons[0]) {
          visibleRadioButtons[0].hidden = true;
          // Simulate click on hidden radio button to trigger all validation logic
          visibleRadioButtons[0].click();
        }
      } else {
        // Use the address entered by the user
        if (visibleRadioButtons[0]) {
          visibleRadioButtons[0].checked = true;
          // Trigger click event to execute all radio button logic
          visibleRadioButtons[0].click();
        }
        this.replaceAddressFieldValues(this.formattedAddresses?.[0], 0);
      }
    }

    // The edit button and close button will reject with the value "stay", specifically to prevent preemptive navigation on drawer stepped forms
    // Get the a11y-dialog container so we can detect backdrop clicks (mousedown directly on the container)
    this.modalValidationCleanup?.();
    const modalContainer = this.modalCloseBtn?.closest('[data-type="modal"]');
    let modalValidationSettled = false;
    let cleanupModalValidationHandlers = () => {};

    const selectUserEnteredAddress = () => {
      if (visibleRadioButtons[0]) {
        visibleRadioButtons[0].checked = true;
        visibleRadioButtons[0].click();
      }
    };

    const stayOnCurrentStep = () => {
      if (modalValidationSettled) return;
      modalValidationSettled = true;
      cleanupModalValidationHandlers();
      this.awaitingAddressSelection = true;
      this.focusStreetAddressAtEnd();
      reject?.({ stay: true });
    };

    const backdropCloseHandler = (event) => {
      if (event.target === modalContainer) {
        selectUserEnteredAddress();
        stayOnCurrentStep();
      }
    };

    const closeHandler = () => {
      selectUserEnteredAddress();
      stayOnCurrentStep();
    };

    const editHandler = () => {
      selectUserEnteredAddress();
      cleanupModalValidationHandlers();
      this.closeVerificationModal();
      stayOnCurrentStep();
    };

    const resolver = () => {
      if (modalValidationSettled) return;
      this.continueButton?.removeEventListener("click", resolver);
      this.continueButton.disabled = true;
      Promise.resolve(this.pendingAddressConfirmation)
        .catch(() => {})
        .then(() => {
          if (modalValidationSettled) return;
          modalValidationSettled = true;
          this.expandableAddrFields && this.handleExpansionFields();
          cleanupModalValidationHandlers();
          this.closeVerificationModal();
          this.awaitingAddressSelection = false;
          this.fieldIsValid = true;
          this.dispatchValidationEvent(true);
          resolve?.();
        });
    };

    cleanupModalValidationHandlers = () => {
      modalContainer?.removeEventListener("mousedown", backdropCloseHandler);
      this.modalCloseBtn?.removeEventListener("click", closeHandler);
      this.editButton?.removeEventListener("click", editHandler);
      this.continueButton?.removeEventListener("click", resolver);
      if (this.modalValidationCleanup === cleanupModalValidationHandlers) {
        this.modalValidationCleanup = null;
      }
    };
    this.modalValidationCleanup = cleanupModalValidationHandlers;
    modalContainer?.addEventListener("mousedown", backdropCloseHandler);
    this.editButton?.addEventListener("click", editHandler);
    this.modalCloseBtn?.addEventListener("click", closeHandler);
    this.continueButton?.addEventListener("click", resolver);
    this.displayValidationModal();
  }

  closeVerificationModal() {
    this.modalCloseBtn
      ?.closest('[data-type="modal"]')
      ?.dispatchEvent(new CustomEvent(MODAL_HIDE_EVENT));
  }

  // If suggested address is in a different country than the selected country, display the country in red.
  // eslint-disable-next-line class-methods-use-this
  compareAddresses(suggested) {
    const countryName = suggested.country
      ? AddressBlock.capitalizeFirst(suggested?.country.toLowerCase())
      : "";
    const countryRegex = new RegExp(`(${countryName})$`, "i");
    let suggestedAddressArray = isSmarty
      ? [
          suggested.street_line,
          suggested.city,
          suggested.state,
          suggested.zipCode
        ]
      : suggested.addr &&
        countryName &&
        suggested?.addr
          ?.trim()
          ?.toLowerCase()
          ?.endsWith(countryName?.trim()?.toLowerCase())
      ? [suggested.addr]
      : [suggested.addr, countryName];
    suggestedAddressArray.forEach((item, index) => {
      if (isSmarty) {
        const regex = new RegExp(item, "g");
        const matchString = this.addrField.value.match(regex);
        if (!matchString) {
          suggestedAddressArray[
            index
          ] = `<span class="${styles.warning}" suggestedAddress>${item}</span>`;
        }
      } else {
        if (isValidateCountry && nonUSCountryMatchFlag === "N") {
          suggestedAddressArray[index] = item.replace(
            countryRegex,
            `<span class="${styles.warning}" suggestedAddress>$1</span>`
          );
        }
      }
    });
    const suggestedAddressString = suggestedAddressArray.join(" ");
    return suggestedAddressString;
  }

  // Used to validate addresses outside of the "United States"
  internationalValidation() {
    let valid = true;
    this.addressFields.forEach((field) => {
      if (
        field &&
        ((field.required && field.checkValidity()) || !field.required)
      ) {
        SteppedForms.setAsValid(field);
      } else if (field) {
        SteppedForms.setAsInvalid(field);
        valid = false;
      }
    });
    return valid;
  }

  setFlags(ISO3) {
    isSmarty = isSmartyCountry(ISO3);
    isValidateCountry =
      (this.preciselyValidateCountries.value === "ALL" ||
        this.preciselyValidateCountries.value.includes(ISO3)) &&
      !this.preciselyExcludeCountries.value.includes(ISO3) &&
      !isSmarty;
  }

  static normalizePinDropComparableValue(value) {
    return String(value || "")
      .replace(/\s+/g, " ")
      .trim();
  }

  buildPinDropComparableAddress(addressOverride) {
    const address = AddressBlock.normalizePinDropComparableValue(
      addressOverride !== undefined ? addressOverride : this.addrField?.value
    );
    const country = AddressBlock.normalizePinDropComparableValue(
      this.getSelectedCountryName()
    );

    if (country && !address.toUpperCase().endsWith(country.toUpperCase())) {
      return AddressBlock.normalizePinDropComparableValue(
        `${address} ${country}`
      );
    }

    return address;
  }

  syncPinDropAddressSubmitted() {
    if (!this.pinDropAddressSubmitted) return;

    if (
      this.pinDropAddressConfirmed?.value !== "true" ||
      !this.pinDropConfirmedAddressSnapshot
    ) {
      this.pinDropAddressSubmitted.value = "false";
      return;
    }

    this.pinDropAddressSubmitted.value =
      this.buildPinDropComparableAddress() ===
      this.pinDropConfirmedAddressSnapshot
        ? "true"
        : "false";
  }

  rememberPinDropConfirmedAddress(address) {
    if (this.pinDropAddressConfirmed) {
      this.pinDropAddressConfirmed.value = "true";
    }

    this.pinDropConfirmedAddressSnapshot =
      this.buildPinDropComparableAddress(address);
    this.syncPinDropAddressSubmitted();
  }

  static capitalizeFirst(string) {
    const words = string?.split(" ");
    return words
      ?.map((w) => w?.slice(0, 1)?.toUpperCase() + w.slice(1))
      ?.join(" ");
  }

  static isDuplicate(addr) {
    const found = nonUSOptionDetailList.some((list) => {
      const country = list?.countryName
        ? this.capitalizeFirst(list?.CountryName?.toLowerCase())
        : list?.country || "";
      return (
        list?.FormattedAddress === addr ||
        list?.FormattedAddress === addr + " " + country ||
        list?.FormattedAddress + " " + country === addr ||
        list?.addr === addr ||
        list?.addr === addr + " " + country ||
        list?.addr + " " + country === addr
      );
    });
    return found;
  }

  getCountries() {
    if (this.countryWrapper) {
      this.countryField.addEventListener("change", () => {
        this.countryOnChange();
      });
      this.setupListeners([this.countryField]);
    } else if (this.countryOverride) {
      if (this.stateWrapper) {
        this.getStates(this.hiddenCountryLocationCode.value);
        this.setupListeners([this.stateField]);
      }
      this.handleFieldDisabling(false);
    }
  }

  updateNonUSFullAddressWithWarning() {
    if (isValidateCountry) {
      let addr = this.addrField?.value.trim();
      let country = this.countryField?.value.trim();
      if (addr?.toLowerCase()?.endsWith(country?.toLowerCase())) {
        const countryRegex = new RegExp(`(${country})$`, "i");
        nonUSFullAddressWithWarning = addr.replace(
          countryRegex,
          `<span class="${styles.warning}" userAddress>$1</span>`
        );
      } else {
        nonUSFullAddressWithWarning = `${addr} <span class="${styles.warning}" userAddress>${country}</span>`;
      }
    }
  }

  countryOnChange() {
    SteppedForms.setAsValid(this.countryField);
    const selected = this.countryField.options[this.countryField.selectedIndex];
    const countryCode = selected?.getAttribute("data-country-code");
    const addresscountryISO3 = selected?.getAttribute("data-country-alpha3");
    this.setFlags(addresscountryISO3);
    this.setAddressFields();
    this.hiddenCountryLocationCode.value = countryCode;
    this.hiddenAddressCountryISO3.value = addresscountryISO3;
    this.addrField.value = "";
    if (!this.isSingleAddressField) {
      if (isValidateCountry) {
        if (this.cityStateZipContainer) {
          this.cityWrapper.hidden = true;
          this.stateWrapper.hidden = true;
          this.zipWrapper.hidden = true;
          this.zipField.value = "";
          this.cityField.value = "";
          this.stateField.value = "";
          this.stateField.removeAttribute("required");
          this.cityField.removeAttribute("required");
        }
        if (this.addr2Wrapper) {
          this.addr2Wrapper.hidden = true;
          this.addrLine2Field.value = "";
        }
        this.updateNonUSFullAddress();
      } else {
        if (this.cityStateZipContainer) {
          this.cityWrapper.hidden = false;
          this.stateWrapper.hidden = false;
          this.zipWrapper.hidden = false;
          this.stateField.setAttribute("required", "");
          this.cityField.setAttribute("required", "");
        }
        this.addr2Wrapper && (this.addr2Wrapper.hidden = false);
        this.revealer && (this.revealer.hidden = false);
        this.addrLine2Area && (this.addrLine2Area.hidden = true);
      }
    }
    this.stateWrapper && !isValidateCountry && this.getStates(countryCode);
    this.hiddenCountryLocationCode.value !== "" &&
      this.handleFieldDisabling(false);
    if (this.countryField.value === "") {
      this.countryField.dataset.validity = "false";
      if (!isValidateCountry) {
        const stateValues = [{ text: "", value: "" }];
        this.stateField && this.createStateSelectBox(stateValues);
      }
    }
    this.syncPinDropAddressSubmitted();
  }

  getStates(countryCode) {
    // Smarty fills the state field with the territory's code, so the territory is its own only state.
    const territoryState = getTerritoryStateCode(
      this.getSelectedCountryAlpha3()
    );
    if (territoryState) {
      this.stateWrapperParent.hidden = false;
      this.stateField.setAttribute("required", "");
      this.createStateSelectBox([
        {
          text: this.getSelectedCountryName() || territoryState,
          value: territoryState
        }
      ]);
      // Must follow createStateSelectBox, which resets the value. No change event: its listener
      // clears fieldIsValid.
      this.stateField.value = territoryState;
      SteppedForms.setAsValid(this.stateField);
      this.territoryStateActive = true;
      this.pendingState = null;
      return;
    }

    if (this.territoryStateActive) {
      this.territoryStateActive = false;
      this.createStateSelectBox([{ text: "", value: "" }]);
    }

    const req = new XMLHttpRequest();
    req.addEventListener("load", () => {
      // A slower response for a previously selected country must not replace the current list.
      if (
        this.hiddenCountryLocationCode &&
        this.hiddenCountryLocationCode.value !== String(countryCode ?? "")
      )
        return;
      const states = req.response;
      let stateValues = [];
      if (req.status !== 200) {
        this.stateWrapperParent.hidden = true;
        this.stateField.removeAttribute("required");
      } else {
        this.stateWrapperParent.hidden = false;
        this.stateField.setAttribute("required", "");
        let valKey = "commonName";
        if (countryCode === "251") {
          valKey = "abbrev";
        }
        stateValues = states.locations.location.map((s) => ({
          text: s.commonName,
          value: s[valKey]
        }));
      }

      stateValues.unshift({ text: "", value: "" });

      this.createStateSelectBox(stateValues);
      if (this.pendingState) {
        this.stateField.value = this.pendingState;
        this.pendingState = null;
        if (this.stateField.value) SteppedForms.setAsValid(this.stateField);
      }
    });

    req.open(
      "GET",
      `${window.PUBLIC_ENV.STATIC_DIRECTORY}/country/state-${countryCode}.json`
    );
    req.responseType = "json";
    req.send();
  }

  createStateSelectBox(stateValues) {
    const newStateSelectBox = selectBox(
      {
        name: "state",
        label: this.stateLabel,
        options: stateValues,
        validate: {
          include: this.stateField.required,
          message: this.stateValidation
        },
        verificationDisplay: false,
        invertTextColor: this.invertTextColor,
        countryList: []
      },
      true
    );

    const template = document.createElement("template");
    template.innerHTML = newStateSelectBox.trim();

    this.updateSelectBoxOptions(this.stateField, template.content.firstChild);

    // Set default to "state"
    this.stateField.value = "";

    this.stateField.addEventListener("change", () => {
      this.stateField.setAttribute("data-changed", "true");
      this.stateField.setAttribute("data-user-modified", "true");
      this.fieldIsValid = false;
      if (isSmarty) {
        if (this.stateField.checkValidity()) {
          SteppedForms.setAsValid(this.stateField);
        } else {
          SteppedForms.setAsInvalid(this.stateField);
        }
      } else SteppedForms.setAsValid(this.stateField);
    });
  }

  // Handles disabling and enabling address fields. When the locID is empty all address fields, except the country field should be disabled, but when it isn't empty fields are enabled
  handleFieldDisabling(disable) {
    this.addressFields.forEach((i) => {
      if (i && i !== this.countryField) i.disabled = disable;
    });
  }

  /* eslint-disable */
  updateSelectBoxOptions = (originalSelectBox, updatedSelectBox) => {
    // Remove options on the original placeholder select box
    [...originalSelectBox.querySelectorAll("option")].forEach((option) => {
      originalSelectBox.removeChild(option);
    });

    // Add real options
    [...updatedSelectBox.querySelectorAll("option")].forEach((option) => {
      originalSelectBox.appendChild(option);
    });
  };
  /* eslint-enable */

  setupAutocompleteOptions() {
    this.autocompleteWrapper.addEventListener("focusout", (e) => {
      // On "focusout", if the new target element isn't inside of the autocomplete, then close the autocomplete
      if (
        e.relatedTarget &&
        !e.relatedTarget.closest("[data-autocomplete-wrapper]")
      )
        this.autocompleteArea.hidden = true;
    });

    document.addEventListener("click", () => {
      this.autocompleteArea.hidden = true;
    });

    this.autocompleteOptions.forEach((o, index) =>
      o.setAttribute("data-autocomplete-option-index", index)
    );

    this.autocompleteOptions.forEach((o) => {
      o.addEventListener("click", () => {
        if (isSmarty) this.selectSmartyCountry(o.data.state);
        this.autocompleteSelected.value = true;
        const addr = isSmarty
          ? this.bundleAddress([o.data.street_line, o.data.city, o.data.state])
          : o.data.FormattedAddress;
        this.expandableAddrFields && this.handleExpansionFields();

        if (this.autocompleteWrapper.hasAttribute("data-single-field")) {
          this.addrField.value = addr;
        } else {
          this.addrField.value = isSmarty
            ? o.data.street_line
            : o.data.FormattedAddress;
          if (!isSmarty && !this.addrField.value.includes(o.data.Country))
            this.addrField.value += ` ${o.data.Country}`;
          this.cityField.value = isSmarty ? o.data.city : "";
          this.stateField.value = isSmarty ? o.data.state : "";
          this.stateField.dispatchEvent(new Event("change", { bubbles: true }));
          this.addressFields.forEach((f) =>
            f?.classList.remove(formStyles.invalid)
          );
        }
        this.updateNonUSFullAddressWithWarning();
        this.setIntlUserInputFieldsFromSelectedAutocompleteOption(o.data);
        nonUSAutoCompleteSelected = o.data;
        if (isSmarty) {
          this.provider.getUSZip(addr).then((zipcode) => {
            if (this.zipField) {
              this.zipField.value = zipcode;
            } else if (this.isSingleAddressField) {
              this.addrField.value += ` ${zipcode}`;
            }
          });
        }
        this.syncPinDropAddressSubmitted();
        this.autocompleteArea.hidden = true;
      });

      o.addEventListener("keyup", (e) => {
        const key = e.keyCode;
        switch (key) {
          case 38:
            this.navigateAutocompleteOptions(
              parseInt(o.getAttribute("data-autocomplete-option-index")) - 1
            );
            break;
          case 40:
            this.navigateAutocompleteOptions(
              parseInt(o.getAttribute("data-autocomplete-option-index")) + 1
            );
            break;
          case 13:
            e.preventDefault();
            this.expandableAddrFields && this.handleExpansionFields();
            o.click();
            break;
        }
      });
    });
  }

  setupAddressField() {
    this.addrField.addEventListener("focus", () => {
      this.setFlags(this.hiddenAddressCountryISO3.value);
      if (isValidateCountry && this.preciselyIK.value.trim()?.length === 0) {
        this.preciselyik();
      }
    });
    /* autocompleteSetting -  (JIRA 7615)
    'off': autocomplete will not fire.
    'unconstrained': autocomplete fires at every keypress.
    'time': autocomplete fires at every ${triggerIntervalInMilliSecond}.
    'char': autocomplete fires at every ${triggerIntervalInChar}.
    autocomplete will wait for ${nbrOfInitialChars} before firing the first autocompletion. */
    this.addrField.addEventListener("input", () => {
      this.updateNonUSFullAddressWithWarning();
      nonUSAutoCompleteSelected = {};

      if (this.suppressNextAutocompleteRequest) {
        this.suppressNextAutocompleteRequest = false;
        return;
      }

      const { timeIntervalCheck } = this.checkIntervals();

      if (this.countryField?.value || this.countryOverride) {
        const countryValue = this.countryOverride
          ? this.countryOverride
          : this.countryField?.value;

        if (this.autocompleteQualification()) {
          // The timeout is used for the time-based interval checking, as soon as a user enters an input a timer is started.
          // If they start typing again it will reset the timer, once the timer is finished it will call the API
          if (this.timeoutId) {
            clearTimeout(this.timeoutId);
            this.timeoutId = false;
          }
          this.timeoutId = setTimeout(
            () => {
              this.provider.autocompleteEndpoint(countryValue);
            },
            timeIntervalCheck ? this.triggerIntervalInMilliSecond?.value : 0
          );
        }
      }
    });

    // Keydown is used instead of keyup due to the tabindex of the inputs
    this.expandableAddrFields &&
      !this.expanded &&
      this.addrField.addEventListener("keydown", (event) => {
        const key = event.keyCode;
        if (key === 13 || key === 9) {
          this.handleExpansionFields();
        }
      });

    this.addrField.addEventListener("keyup", (event) => {
      const key = event.keyCode;
      if (key === 40) {
        this.navigateAutocompleteOptions(0);
      }
      // catch other events if they don't finish the auto complete, expand fields
      this.expandableAddrFields &&
        !this.expanded &&
        this.addrField.value &&
        this.addrField.addEventListener("blur", () => {
          this.handleExpansionFields();
          this.expanded = true;
        });
    });

    if (this.revealer) {
      this.revealer.addEventListener("click", () => {
        if (this.addrLine2Area) {
          this.addrLine2Area.hidden = false;
        }
        if (this.addrLine2Input) {
          this.addrLine2Input.focus();
        }
        this.revealer.hidden = true;
      });
    }
  }

  static formatGeolocatedAddress(match, position) {
    const firstString = (...values) =>
      values.find((value) => typeof value === "string" && value.trim())?.trim();

    const formatGoogleAddressComponents = (components = []) => {
      if (!Array.isArray(components) || components.length === 0) return "";

      const getComponent = (type, key = "long_name") =>
        components.find((component) => component?.types?.includes(type))?.[key];

      const streetLine = [getComponent("street_number"), getComponent("route")]
        .filter(Boolean)
        .join(" ");
      const locality = getComponent("locality");
      const region = getComponent("administrative_area_level_1", "short_name");
      const postalCode = getComponent("postal_code");
      const country = getComponent("country");

      return [streetLine, locality, region, postalCode, country]
        .filter(Boolean)
        .join(", ");
    };

    const formatAddressObject = (address) => {
      if (!address || typeof address !== "object") return "";

      const fullAddress = firstString(
        address.formatted,
        address.formattedAddress,
        address.address,
        address.label
      );

      if (fullAddress) {
        return fullAddress;
      }

      const streetLine = firstString(
        address.street1,
        address.streetAddress,
        address.street,
        [address.streetNumber, address.route].filter(Boolean).join(" "),
        [address.houseNumber, address.streetName].filter(Boolean).join(" "),
        address.line1,
        address.addressLine1,
        address.deliveryAddress
      );

      const localityLine = [
        firstString(address.locality, address.city),
        firstString(address.region, address.state),
        firstString(address.postalCode, address.zipCode),
        firstString(address.country)
      ]
        .filter(Boolean)
        .join(", ");

      return [streetLine, localityLine].filter(Boolean).join(", ");
    };

    const address = match?.address;
    if (typeof address === "string" && address.trim()) {
      return address.trim();
    }

    const googleFormatted = firstString(
      match?.formatted_address,
      formatGoogleAddressComponents(match?.address_components)
    );

    if (googleFormatted) {
      return googleFormatted;
    }

    const topLevelAddress = firstString(
      match?.formattedAddress,
      match?.label,
      match?.displayName,
      match?.name
    );

    if (topLevelAddress) {
      return topLevelAddress;
    }

    if (address && typeof address === "object") {
      const formatted = formatAddressObject(address);

      if (typeof formatted === "string" && formatted.trim()) {
        return formatted.trim();
      }
    }

    const nestedFallback = firstString(
      formatAddressObject(match?.facility?.address),
      formatAddressObject(match?.parent?.address),
      formatAddressObject(match?.facility),
      formatAddressObject(match?.parent)
    );

    if (nestedFallback) {
      return nestedFallback;
    }

    return `${position.coords.latitude}, ${position.coords.longitude}`;
  }

  static selectBestGeolocatedMatch(results) {
    const matches = Array.isArray(results)
      ? results
      : Array.isArray(results?.results)
      ? results.results
      : results
      ? [results]
      : [];

    if (!matches.length) return null;

    const scoreMatch = (match) => {
      const types = Array.isArray(match?.types) ? match.types : [];
      if (
        types.includes("street_address") ||
        types.includes("premise") ||
        types.includes("subpremise")
      ) {
        return 5;
      }
      if (types.includes("route")) {
        return 4;
      }
      if (
        types.includes("intersection") ||
        types.includes("establishment") ||
        types.includes("point_of_interest")
      ) {
        return 3;
      }
      if (types.includes("postal_code")) {
        return 2;
      }
      if (
        types.includes("locality") ||
        types.includes("administrative_area_level_1") ||
        types.includes("country")
      ) {
        return 1;
      }
      return 0;
    };

    return matches.reduce((bestMatch, currentMatch) => {
      if (!bestMatch) return currentMatch;

      return scoreMatch(currentMatch) > scoreMatch(bestMatch)
        ? currentMatch
        : bestMatch;
    }, null);
  }

  static getGeolocatedCountryData(result) {
    const address = result?.raw?.address || result?.raw || {};

    return {
      name: address?.country,
      alpha3: address?.countryCode3,
      alpha2: address?.countryCode2,
      code: address?.countryId ? String(address.countryId) : ""
    };
  }

  syncGeolocatedCountry(result) {
    this.syncAddressCountry(AddressBlock.getGeolocatedCountryData(result));
  }

  syncAddressCountry(country) {
    if (!country) return;

    if (!this.countryField) {
      if (this.countryOverrideField && country.name) {
        this.countryOverrideField.value = country.name;
        this.countryOverride = country.name;
      }
      if (this.hiddenCountryLocationCode && country.code) {
        this.hiddenCountryLocationCode.value = country.code;
      }
      if (this.hiddenAddressCountryISO3 && country.alpha3) {
        this.hiddenAddressCountryISO3.value = country.alpha3;
        this.setFlags(country.alpha3);
        this.setAddressFields();
      }
      return;
    }

    const options = Array.from(this.countryField.options || []);
    const matchingOption = options.find((option) => {
      const optionCode = option.getAttribute("data-country-code");
      const optionAlpha3 = option.getAttribute("data-country-alpha3");
      const optionValue = option.value?.toLowerCase();

      return (
        (country.alpha3 && optionAlpha3 === country.alpha3) ||
        (country.code && optionCode === country.code) ||
        (country.name && optionValue === country.name.toLowerCase())
      );
    });

    if (!matchingOption) return;

    this.countryField.value = matchingOption.value;
    this.hiddenCountryLocationCode.value =
      matchingOption.getAttribute("data-country-code") || "";
    this.hiddenAddressCountryISO3.value =
      matchingOption.getAttribute("data-country-alpha3") || "";
    this.setFlags(this.hiddenAddressCountryISO3.value);
    this.setAddressFields();
    SteppedForms.setAsValid(this.countryField);
  }

  stripPinDropCountryFromAddress(address, pinDropCountry) {
    let cleanedAddress = address;
    const selected = this.countryField?.selectedOptions?.[0];
    const countryPatterns = [
      selected?.value,
      selected?.getAttribute("data-country-code"),
      selected?.getAttribute("data-country-alpha3"),
      pinDropCountry?.name,
      pinDropCountry?.alpha2,
      pinDropCountry?.alpha3,
      pinDropCountry?.code
    ]
      .filter(Boolean)
      .map((c) => c.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));

    if (countryPatterns.length > 0) {
      const regex = new RegExp(`,\\s*(${countryPatterns.join("|")})\\s*$`, "i");
      cleanedAddress = address.replace(regex, "").trim();
    }

    return cleanedAddress;
  }

  async lookupGeolocatedAddress(position) {
    // Configure MapsService using singleton pattern
    if (!configureMapsService()) {
      throw new Error("Failed to configure MapsService for geolocation");
    }

    const results = await MapsService.reverseGeocode(
      {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude
      },
      {
        providers: [MapProviderType.GOOGLE],
        limit: 1,
        language: this.mapsLanguage,
        unconverted: true
      }
    );

    const match = AddressBlock.selectBestGeolocatedMatch(results);
    const address = AddressBlock.formatGeolocatedAddress(match, position);

    return {
      address,
      coordinates: {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude
      },
      country: PinDropMap.getGeocodeResultCountry(match),
      zoom: PinDropMap.getZoomForGeocodeResult(match),
      raw: match
    };
  }

  static isSaveableAddressLocation(result) {
    return PinDropMap.validateAddressLocation(result);
  }

  static formatPinDropAddress(result, coordinates) {
    const firstString = (...values) =>
      values.find((value) => typeof value === "string" && value.trim())?.trim();

    const address = result?.address;
    const addressLine = firstString(
      result?.formatted,
      result?.formattedAddress,
      result?.formatted_address,
      address?.formatted,
      address?.formattedAddress,
      address?.label,
      result?.label,
      result?.displayName,
      result?.name
    );

    if (addressLine) return addressLine;

    const street = firstString(result?.street1, address?.street1);
    const city = firstString(result?.city, address?.city, address?.locality);
    const state = firstString(result?.state, address?.state, address?.region);
    const postal = firstString(
      result?.zipCode,
      result?.postalCode,
      address?.zipCode,
      address?.postalCode
    );
    const country = firstString(result?.country, address?.country);
    const parts = [street, city, state, postal, country].filter(Boolean);

    if (parts.length) return parts.join(", ");

    return AddressBlock.formatGeolocatedAddress(
      result,
      coordinates
        ? { coords: coordinates }
        : { coords: { latitude: 0, longitude: 0 } }
    );
  }

  getSelectedCountryName() {
    return this.countryField?.value || this.countryOverride || "";
  }

  getSelectedCountryAlpha3() {
    return (
      this.hiddenAddressCountryISO3?.value ||
      this.countryField?.selectedOptions?.[0]?.getAttribute(
        "data-country-alpha3"
      ) ||
      ""
    );
  }

  getSelectedCountryCode() {
    return (
      this.hiddenCountryLocationCode?.value ||
      this.countryField?.selectedOptions?.[0]?.getAttribute(
        "data-country-code"
      ) ||
      ""
    );
  }

  getSelectedCountryContext() {
    return {
      name: this.getSelectedCountryName(),
      alpha3: this.getSelectedCountryAlpha3(),
      code: this.getSelectedCountryCode()
    };
  }

  buildPinDropGeocodeQuery(address, countryContext) {
    const query = String(address || "").trim();
    const selectedCountryName = (
      countryContext?.name || this.getSelectedCountryContext().name
    )?.trim();

    if (!query || !selectedCountryName) {
      return query;
    }

    if (
      query.toUpperCase().endsWith(selectedCountryName.toUpperCase()) ||
      this.queryIncludesCountry(query)
    ) {
      return query;
    }

    return `${query}, ${selectedCountryName}`;
  }

  queryIncludesCountry(query) {
    const options = Array.from(this.countryField?.options || []);
    const countryValues = options.flatMap((option) => [
      option.value,
      option.getAttribute("data-country-alpha3")
    ]);

    if (this.countryOverride) {
      countryValues.push(this.countryOverride);
      countryValues.push(this.hiddenAddressCountryISO3?.value);
    }

    return countryValues.filter(Boolean).some((value) =>
      PinDropMap.queryIndicatesCountry(query, {
        name: value,
        alpha3: value
      })
    );
  }

  pinDropResultMatchesQueryContext(
    result,
    query,
    countryContext = this.getSelectedCountryContext()
  ) {
    const resultMatchesCountry = PinDropMap.geocodeResultMatchesCountry(
      result,
      countryContext?.alpha3,
      countryContext?.code,
      countryContext?.name
    );
    const resultCountry = PinDropMap.getGeocodeResultCountry(result);
    const queryIndicatesResultCountry = PinDropMap.queryIndicatesCountry(
      query,
      resultCountry
    );

    return resultMatchesCountry || queryIndicatesResultCountry;
  }

  static normalizeServiceCoordinates(coordinates) {
    if (!coordinates) return null;

    const longitude = Array.isArray(coordinates)
      ? coordinates[0]
      : coordinates.longitude ?? coordinates.lng;
    const latitude = Array.isArray(coordinates)
      ? coordinates[1]
      : coordinates.latitude ?? coordinates.lat;
    const numericLatitude = Number(latitude);
    const numericLongitude = Number(longitude);

    if (
      !Number.isFinite(numericLatitude) ||
      !Number.isFinite(numericLongitude) ||
      numericLatitude < -90 ||
      numericLatitude > 90 ||
      numericLongitude < -180 ||
      numericLongitude > 180
    ) {
      return null;
    }

    return {
      latitude: numericLatitude,
      longitude: numericLongitude
    };
  }

  async geocodeCountryCenter(countryName) {
    if (!countryName) return null;

    const countryGeocodeResults = await MapsService.geocodeAddress(
      countryName,
      {
        providers: [MapProviderType.GOOGLE],
        limit: 1,
        language: this.mapsLanguage,
        unconverted: true
      }
    );

    if (
      !countryGeocodeResults?.length ||
      !countryGeocodeResults[0]?.coordinates
    ) {
      return null;
    }

    return AddressBlock.normalizeServiceCoordinates(
      countryGeocodeResults[0].coordinates
    );
  }

  async geocodeAddressCandidate(address, countryContext) {
    if (!address) return null;

    const addressQuery = this.buildPinDropGeocodeQuery(address, countryContext);

    const geocodeResults = await MapsService.geocodeAddress(addressQuery, {
      providers: [MapProviderType.GOOGLE],
      limit: 1,
      language: this.mapsLanguage,
      unconverted: true
    });

    if (!geocodeResults?.length || !geocodeResults[0]?.coordinates) {
      return null;
    }

    const result = geocodeResults[0];
    const isSaveableAddressLocation =
      AddressBlock.isSaveableAddressLocation(result);

    if (
      !this.pinDropResultMatchesQueryContext(result, address, countryContext)
    ) {
      return null;
    }

    const resultCountry = PinDropMap.getGeocodeResultCountry(result);
    const coordinates = AddressBlock.normalizeServiceCoordinates(
      result.coordinates
    );

    if (!coordinates) {
      return null;
    }

    return {
      coordinates,
      address: AddressBlock.formatPinDropAddress(result, coordinates),
      isSaveableAddressLocation,
      country: resultCountry,
      zoom: PinDropMap.getZoomForGeocodeResult(result),
      raw: result
    };
  }

  static async getIpLocationCandidate() {
    const ipLocation = await geolocate();

    const coordinates = AddressBlock.normalizeServiceCoordinates(
      ipLocation?.coordinates
    );

    if (!coordinates) {
      return null;
    }

    return {
      coordinates,
      address: AddressBlock.formatPinDropAddress(ipLocation, coordinates),
      isSaveableAddressLocation:
        AddressBlock.isSaveableAddressLocation(ipLocation),
      country: PinDropMap.getGeocodeResultCountry(ipLocation),
      zoom: PinDropMap.getZoomForGeocodeResult(ipLocation),
      raw: ipLocation
    };
  }

  setupDefaultCountry(detectedCountry) {
    if (detectedCountry) {
      const countryOption = this.countryField.querySelector(
        `[data-country-alpha3=${detectedCountry}]`
      );
      if (countryOption) {
        this.countryField.value = countryOption.value;
        this.countryField.dispatchEvent(new Event("change", { bubbles: true }));
      } else this.countryField.selectedIndex = 0;
    }
  }

  autocompleteQualification() {
    const { firstAutocompleteCheck, timeIntervalCheck, charIntervalCheck } =
      this.checkIntervals();

    return (
      (isSmarty || isValidateCountry) &&
      this.autocompleteSetting?.value !== "off" &&
      this.emptyStrikes < this.strikeLimit &&
      (this.autocompleteSetting?.value === "unconstrained" ||
        firstAutocompleteCheck ||
        timeIntervalCheck ||
        charIntervalCheck)
    );
  }

  checkIntervals() {
    const valueLength = this.addrField?.value?.length;
    const pastInitialLimit = valueLength >= this.nbrOfInitialChars;

    const firstAutocompleteCheck = pastInitialLimit && !this.timeoutId;
    const timeIntervalCheck =
      this.autocompleteSetting?.value === "time" &&
      pastInitialLimit &&
      this.timeoutId;
    const charIntervalCheck =
      this.autocompleteSetting?.value === "char" &&
      pastInitialLimit &&
      valueLength % this.triggerIntervalInChar === 0 &&
      this.timeoutId;
    return { firstAutocompleteCheck, timeIntervalCheck, charIntervalCheck };
  }

  setupListeners(fields) {
    fields.forEach((field) => {
      field?.addEventListener("focus", () => {
        field.classList.remove(formStyles.valid, formStyles.invalid);
      });

      field?.addEventListener("input", () => {
        field.value = cleanFieldValue(field.value);
        if (field.value.trim() === "") {
          // If only spaces remain, clear the field
          field.value = "";
        }
        if (field.value !== "" && field.value !== " ") {
          this.autocompleteSelected.value = "false";
          this.validatedAddressSelected.value = "false";
          this.validationAutocompleteSelected.value = "false";
          field.setAttribute("data-changed", "true");
          field.setAttribute("data-user-modified", "true");
          this.fieldIsValid = false;
          this.awaitingAddressSelection = false;
        }
        this.syncPinDropAddressSubmitted();
      });

      field?.addEventListener("blur", () => {
        if (field) {
          field.value = field.value.trim();
        }
        // Trim leading/trailing spaces when the user leaves the field
      });
    });
  }

  setupPinDropFlow() {
    const pinDropEnabled =
      this.isSingleAddressField &&
      this.enablePinDrop?.value === "true" &&
      this.pinDropRevealer;

    if (!pinDropEnabled) return;

    const openPinDropModal = async () => {
      try {
        if (this.pinDropLinkClicked) {
          this.pinDropLinkClicked.value = "true";
        }

        // Configure once up front so all MapsService calls in this flow
        // use the correct non-prod/prod endpoint selection.
        const mapsConfigured = configureMapsService();

        let coordinates = null;
        let precision = "address"; // Default precision level
        let initialZoom = null;
        let initialAddress = "";
        let initialCountry = null;
        let initialIsValid = false;
        let initialErrorMessage =
          this.pinDropModalInvalidLocationMessage?.value;
        let ipCandidate = null;

        const currentAddress = this.addrField?.value?.trim();
        const selectedCountryContext = this.getSelectedCountryContext();
        const selectedCountry = selectedCountryContext.name;
        const selectedCountryAlpha3 = selectedCountryContext.alpha3;
        let effectiveGeoCountryAlpha3 = this.geoCountry || "";

        if (!mapsConfigured) {
          initialErrorMessage = this.pinDropMapError?.value;
          PinDropMap.logMapError(initialErrorMessage, null, {
            reason: "maps-service-config"
          });
        } else if (!currentAddress) {
          try {
            ipCandidate = await AddressBlock.getIpLocationCandidate();
            effectiveGeoCountryAlpha3 =
              effectiveGeoCountryAlpha3 ||
              ipCandidate?.raw?.address?.countryCode3 ||
              "";
          } catch (ipCountryError) {
            // Continue with the selected form country when IP lookup fails.
          }
        }

        const geoCountryMatchesFormCountry = Boolean(
          effectiveGeoCountryAlpha3 &&
            selectedCountryAlpha3 &&
            effectiveGeoCountryAlpha3 === selectedCountryAlpha3
        );

        if (!mapsConfigured) {
          initialAddress = currentAddress || "";
          initialIsValid = false;
        } else if (currentAddress) {
          try {
            const addressCandidate = await this.geocodeAddressCandidate(
              currentAddress,
              selectedCountryContext
            );

            if (addressCandidate?.coordinates) {
              coordinates = addressCandidate.coordinates;
              precision = "address";
              initialZoom = addressCandidate.zoom;
              initialAddress = addressCandidate.address || currentAddress;
              initialCountry = addressCandidate.country;
              initialIsValid = addressCandidate.isSaveableAddressLocation;
            }
          } catch (error) {
            // Fall through to country-center fallback.
          }

          if (!coordinates) {
            try {
              const countryCoordinates = await this.geocodeCountryCenter(
                selectedCountry
              );
              if (countryCoordinates) {
                coordinates = countryCoordinates;
                precision = "country";
                initialZoom = PinDropMap.getZoomForPrecision("country");
              }
            } catch (countryError) {
              // Leave coordinates unset so the common error path handles it.
            }
          }
        } else if (geoCountryMatchesFormCountry) {
          try {
            if (!navigator.geolocation) {
              throw { code: 0 };
            }

            const position = await new Promise((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(resolve, reject, {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 600000
              });
            });
            const gpsCandidate = await this.lookupGeolocatedAddress(position);

            coordinates = gpsCandidate.coordinates;
            precision = "geolocation";
            initialZoom =
              gpsCandidate.zoom || PinDropMap.getZoomForPrecision(precision);
            const gpsIsSaveableAddress = AddressBlock.isSaveableAddressLocation(
              gpsCandidate.raw
            );
            initialAddress = gpsIsSaveableAddress ? gpsCandidate.address : "";
            initialCountry = gpsCandidate.country;
            initialIsValid =
              gpsIsSaveableAddress && Boolean(gpsCandidate.address);
          } catch (gpsError) {
            try {
              ipCandidate =
                ipCandidate || (await AddressBlock.getIpLocationCandidate());
              if (ipCandidate?.coordinates) {
                coordinates = ipCandidate.coordinates;
                precision = "ip";
                initialZoom =
                  ipCandidate.zoom || PinDropMap.getZoomForPrecision(precision);
                initialAddress = ipCandidate.isSaveableAddressLocation
                  ? ipCandidate.address
                  : "";
                initialCountry = ipCandidate.country;
                initialIsValid =
                  ipCandidate.isSaveableAddressLocation &&
                  Boolean(ipCandidate.address);
              }
            } catch (ipError) {
              // Fall through to country-center fallback.
            }

            // If user/IP location is unavailable, use the selected country.
            if (!coordinates) {
              const countryCoordinates = await this.geocodeCountryCenter(
                selectedCountry
              );
              if (countryCoordinates) {
                coordinates = countryCoordinates;
                precision = "country";
                initialZoom = PinDropMap.getZoomForPrecision("country");
              }
            }
          }
        } else {
          const countryCoordinates = await this.geocodeCountryCenter(
            selectedCountry
          );
          if (countryCoordinates) {
            coordinates = countryCoordinates;
            precision = "country";
            initialZoom = PinDropMap.getZoomForPrecision("country");
          }
        }

        if (!coordinates) {
          initialErrorMessage =
            this.geolocateLookupError?.value || this.pinDropMapError?.value;
          PinDropMap.logMapError(initialErrorMessage, null, {
            reason: "initial-coordinate-lookup"
          });
          initialAddress = initialAddress || currentAddress || "";
          initialIsValid = false;
        }

        // Show the pin drop modal
        this.pinDropModalController.show({
          initialCoordinates: coordinates,
          precision: precision,
          initialZoom,
          initialAddress,
          initialCountry,
          initialIsValid,
          initialErrorMessage,
          onGeolocateFallback: () => AddressBlock.getIpLocationCandidate(),
          onAccept: (address, coordinates, pinDropCountry) => {
            // User confirmed the address from the pin
            this.syncAddressCountry(pinDropCountry);
            // Strip country information from the end since it's already in the country field.
            const cleanedAddress = this.stripPinDropCountryFromAddress(
              address,
              pinDropCountry
            );

            this.suppressNextAutocompleteRequest = true;
            this.addrField.value = cleanedAddress;
            this.rememberPinDropConfirmedAddress(cleanedAddress);
            this.addrField.dispatchEvent(new Event("input", { bubbles: true }));
            this.addrField.dispatchEvent(
              new Event("change", { bubbles: true })
            );
            this.addrField.focus();
            this.autocompleteArea.hidden = true;

            this.closeVerificationModal();
          }
        });
      } catch (error) {
        this.addrField?.focus();
      }
    };

    this.pinDropRevealer?.addEventListener("click", openPinDropModal);
  }

  setupVerificationListeners() {
    for (let i = 0; i < this.radioButtons?.length; i++) {
      this.radioButtons[i].addEventListener("click", (event) => {
        const btn = event.currentTarget;
        const btnIndex = Array.from(this.radioButtons).indexOf(btn);
        this.continueButton.disabled = false;
        this.replaceAddressFieldValues(this.formattedAddresses?.[btnIndex], i);
        this.fieldIsValid = true;
        this.pendingAddressConfirmation = this.confirmedInputAddress(
          this.formattedAddresses?.[i],
          i
        );
      });
    }
  }

  getAutocompleteAPI(countryValue) {
    return this.provider.getAutocompleteAPI(countryValue);
  }

  static getAddressVerifyAPI(address, countryValue, countryComparisonFlag) {
    return getLegacyAddressVerifyAPI({
      address,
      countryValue,
      countryComparisonFlag,
      isSmarty
    });
  }

  static getUSZip() {
    return Promise.resolve("");
  }

  autocompleteEndpoint(countryValue) {
    this.provider.autocompleteEndpoint(countryValue);
  }

  // eslint-disable-next-line class-methods-use-this
  bundleAddress(addressArray) {
    return addressArray
      .map((addressPart) => addressPart?.trim())
      .filter(Boolean)
      .join(" ");
  }

  focusStreetAddressAtEnd() {
    const focusInput = () => {
      if (!this.addrField) return;

      this.addrField.focus();

      if (typeof this.addrField.setSelectionRange === "function") {
        const endPosition = this.addrField.value?.length || 0;
        this.addrField.setSelectionRange(endPosition, endPosition);
      }
    };

    if (typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(focusInput);
      });
    } else {
      window.setTimeout(focusInput, 0);
    }
  }

  displayValidationModal() {
    const elem = document.createElement("div");
    elem.dataset.remoteTriggerId = this.element.dataset.modalremotetrigger;
    remoteComponentPublish(elem);
    this.awaitingAddressSelection = true;
  }

  // eslint-disable-next-line class-methods-use-this
  processOptions(currentAddress, suggestedAddresses, origin, list) {
    let address,
      optionalValues = {};
    let country = "";
    if (origin === "addressVerify") {
      if (suggestedAddresses?.Output?.length > 0) {
        for (let i = 0; i < suggestedAddresses?.Output?.length; i++) {
          address = suggestedAddresses?.Output[i];
          country = address.CountryName
            ? AddressBlock.capitalizeFirst(address.CountryName.toLowerCase())
            : "";
          if (
            address?.Status !== "F" &&
            currentAddress !== address?.FormattedAddress &&
            address?.FormattedAddress?.length > 0
          ) {
            optionalValues = {
              addr: address?.FormattedAddress?.trim()
                ?.toLowerCase()
                ?.endsWith(country.trim()?.toLowerCase())
                ? address.FormattedAddress
                : `${address.FormattedAddress} ${country}`,
              country: country,
              source: origin
            };
            const isDuplicated = AddressBlock.isDuplicate(
              address.FormattedAddress
            );
            if (!isDuplicated) {
              list.push(optionalValues);
              nonUSOptionDetailList.push(address);
            }
          }
        }
      }
    } else {
      const len =
        suggestedAddresses?.Output?.length > 2
          ? 2
          : suggestedAddresses?.Output?.length;
      for (let i = 0; i < len; i++) {
        address = suggestedAddresses?.Output[i];
        country = address.Country
          ? AddressBlock.capitalizeFirst(address.Country.toLowerCase())
          : "";
        if (
          currentAddress !== address?.FormattedAddress &&
          address?.FormattedAddress?.length > 0
        ) {
          optionalValues = {
            addr: address.FormattedAddress.trim()
              ?.toLowerCase()
              ?.endsWith(country.trim()?.toLowerCase())
              ? address.FormattedAddress
              : `${address.FormattedAddress} ${country}`,
            country: country,
            source: origin
          };
          const isDuplicated = AddressBlock.isDuplicate(
            address.FormattedAddress
          );
          if (!isDuplicated) {
            list.push(optionalValues);
            nonUSOptionDetailList.push(address);
          }
        }
      }
    }
  }

  async formatAddressOptions(suggestedValues) {
    // creates the address option list for the user to choose from.
    // suggestedValues is the suggested address from address verification api.
    let addressOptions = [];
    nonUSOptionDetailList = [];
    // The first option is the user's input address.
    addressOptions.push({
      addr: DOMPurify.sanitize(this.addrField.value.trim(), {
        ALLOWED_TAGS: []
      }),
      city: DOMPurify.sanitize(this.cityField?.value.trim(), {
        ALLOWED_TAGS: []
      }),
      state: DOMPurify.sanitize(this.stateField?.value.trim(), {
        ALLOWED_TAGS: []
      }),
      zipCode: DOMPurify.sanitize(this.zipField?.value.trim(), {
        ALLOWED_TAGS: []
      }),
      country: DOMPurify.sanitize(this.countryField?.value.trim(), {
        ALLOWED_TAGS: []
      })
    });
    if (isValidateCountry) nonUSOptionDetailList.push(addressOptions[0]);
    if (isSmarty) {
      // For USA, handle both SmartyStreets verification and autocomplete responses
      if (suggestedValues && suggestedValues?.length > 0) {
        // Check if this is a SmartyStreets verification response or autocomplete response
        const isVerificationResponse =
          suggestedValues[0]?._responseType === "verification";

        if (isVerificationResponse) {
          // SmartyStreets verification response format
          suggestedValues.forEach((verificationResult) => {
            addressOptions.push({
              street_line: verificationResult.delivery_line_1,
              city: verificationResult.components?.city_name,
              state: verificationResult.components?.state_abbreviation,
              zipCode: verificationResult.components?.zipcode,
              source: "addressVerify",
              isSmarty: true
            });
          });
        } else {
          // Autocomplete response format (original logic)
          const addressPromises = suggestedValues.map(async (address) => {
            const addressParts = [
              address["street_line"],
              address["city"],
              address["state"]
            ];

            const zipcode = await this.provider.getUSZip(
              this.bundleAddress(addressParts)
            );

            return {
              street_line: address["street_line"],
              city: address["city"],
              state: address["state"],
              zipCode: zipcode,
              source: "autocomplete",
              isSmarty: true
            };
          });

          // Wait for all zipcodes to be fetched
          const resolvedAddresses = await Promise.all(addressPromises);
          addressOptions.push(...resolvedAddresses);
        }
      }
    } else if (isValidateCountry) {
      // Process NON USA options add addresss suggested by address verify api
      if (suggestedValues?.Output?.length > 0)
        this.processOptions(
          this.addrField.value,
          suggestedValues,
          "addressVerify",
          addressOptions
        );
      // For non USA, we add more options from autocomplete suggestions
      if (nonUSAutoCompleteResponse?.Output?.length > 0)
        this.processOptions(
          this.addrField.value,
          nonUSAutoCompleteResponse,
          "autocomplete",
          addressOptions
        );
      // For non USA, we add more options from autocomplete suggestions using address country, not ISO3
      if (nonUSAutoCompleteResponseAddrCountry?.Output?.length > 0)
        this.processOptions(
          this.addrField.value,
          nonUSAutoCompleteResponseAddrCountry,
          "autocomplete",
          addressOptions
        );
    }
    return addressOptions;
  }

  // Smarty searches every Smarty country at once, so a suggestion can belong to a different one
  // than the dropdown (a mainland address picked under Puerto Rico). The dropdown follows the
  // suggestion so the submitted countryISO3/locID match the address. countryOnChange clears the
  // address fields, so callers fill them afterwards.
  selectSmartyCountry(state) {
    const iso3 = getSmartyCountryForState(state);
    if (!this.countryWrapper || iso3 === this.getSelectedCountryAlpha3())
      return;
    const option = this.countryField.querySelector(
      `[data-country-alpha3="${iso3}"]`
    );
    if (!option) return;
    this.pendingState = state;
    this.countryField.value = option.value;
    this.countryField.dispatchEvent(new Event("change", { bubbles: true }));
  }

  replaceAddressFieldValues(newAddressValues, index) {
    const streetValue =
      newAddressValues?.street_line || newAddressValues?.addr || "";

    if (newAddressValues?.isSmarty) {
      this.selectSmartyCountry(newAddressValues.state);
    } else if (
      this.countryWrapper &&
      newAddressValues?.country &&
      this.countryField.value !== newAddressValues?.country
    ) {
      this.countryField.value = newAddressValues?.country;
      // "You Entered" after a suggestion pick switched Smarty countries also needs the change
      // event, or the hidden countryISO3/locID keep the suggestion's country.
      if (isSmarty) this.pendingState = newAddressValues?.state;
      if (isValidateCountry || isSmarty)
        this.countryField.dispatchEvent(new Event("change", { bubbles: true }));
    }
    if (isSmarty) {
      const addressParts = [
        streetValue,
        newAddressValues?.city,
        newAddressValues?.state,
        newAddressValues?.zipCode
      ];
      if (!this.isSingleAddressField) {
        this.addrField.value = streetValue;
        this.cityField.value = newAddressValues?.city;
        this.stateField.value = newAddressValues?.state;
        this.zipField.value = newAddressValues?.zipCode || "";
        this.stateField.dispatchEvent(new Event("change", { bubbles: true }));
        if (
          this.addrLine2Field &&
          streetValue
            .toLowerCase()
            .includes((this.addrLine2Field.value || "").toLowerCase()) &&
          index === 1
        )
          // If Unit, Apt, or Suite is added in address line 2, Smarty will combine address line2 with the street address and returns it in delivery_line_1.
          // Therefore if the user accepts the suggested address by Smarty, and if the street address already contains address line 2 data,
          // we blank out address line 2 to prevent duplicated data.
          this.addrLine2Field.value = "";
      } else {
        this.addrField.value = this.bundleAddress([...addressParts]);
      }
    } else {
      this.addrField.value = this.bundleAddress([newAddressValues?.addr]);
    }
    this.syncPinDropAddressSubmitted();
  }

  getValues = () => {
    const values = [];
    let cityLine = "";

    if (this.cityField && this.cityField.value !== "") {
      cityLine += this.cityField.value;

      if (this.stateField && this.stateField.value !== "") {
        cityLine += `, ${this.stateField.value}`;
      }
    } else if (this.stateField && this.stateField.value !== "") {
      cityLine += this.stateField.value;
    }

    if (this.zipField && this.zipField.value !== "") {
      cityLine += ` ${this.zipField.value}`;
    }

    const address =
      this.addrField && this.addrField.value !== "" ? this.addrField.value : "";
    const line2 =
      this.addrLine2Field && this.addrLine2Field.value !== ""
        ? this.addrLine2Field.value
        : "";

    if (address !== "") values.push(`${address} ${line2}`.trim());
    if (cityLine !== "") values.push(cityLine);

    if (this.countryOverride && this.countryOverride !== "") {
      values.push(this.countryOverride);
    } else if (this.countryField && this.countryField.value !== "") {
      values.push(this.countryField.value);
    }
    return values;
  };

  dispatchValidationEvent(valid) {
    this.addrField.dispatchEvent(
      new CustomEvent("field-validated", {
        bubbles: true,
        detail: { fieldType: "address", valid }
      })
    );
  }

  checkIsValid = () => {
    if (!this.addrField.required && this.addrField.value === "") {
      return true;
    } else if (isSmarty || isValidateCountry) {
      return !this.awaitingAddressSelection && this.fieldIsValid
        ? true
        : new Promise((resolve, reject) => {
            this.addressValidation(resolve, reject);
          });
    } else {
      return this.internationalValidation();
    }
  };
}

export const init = () => {
  initComponent("address-block", (element) => new AddressBlock(element));
};
init();
